import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  Link,
  CircularProgress,
  Button,
  Grid,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SwitchLessonsDrawer from "./SwitchLessonsDrawer";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TeachersGuide from "./TeachersGuide";
import { getTrackedLessons } from "./functions";
import FolderIcon from "@mui/icons-material/FolderOpen";
import LessonCard from "./LessonCard";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import { keyframes } from "@mui/system";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LightbulbIcon from "@mui/icons-material/LightbulbOutlined";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";

import {
  lessonUrlMapping,
  getLessonTypeParam,
  lessonVideoMapping,
} from "./functions";
import QuizBank from "./QuizBank";
import { useNavigate, useLocation } from "react-router-dom";
import FlashcardsBank from "./FlashcardsBank";
import LessonBankDialog from "./LessonBankDialog";
import VideoDialog from "./VideoDialog";
import HelpDialog from "./HelpDialog";

const InfoBox = ({
  icon: Icon,
  title,
  instructions,
  theme,
  styles,
  teachersGuide = null,
  user = null,
  lessonType = null,
  quiz = false,
  flashcards = false,
  textLabbet = false,
  grading = false,
  powerpoint = false,
  assistent = false,
  vision = false,
  lessonPlan = false,
  podcast = false,
  hp = false,

  showExampleButton,
  onExampleClick,
  loadingExample,
}) => {
  const muiTheme = useTheme();
  const [openLessonBank, setOpenLessonBank] = useState(false);
  const [openVideoDialog, setOpenVideoDialog] = useState(false);
  const [switchLessonDrawerOpen, setSwitchLessonDrawerOpen] = useState(false);
  const [openQuizBank, setOpenQuizBank] = useState(false);
  const [showAnimation, setShowAnimation] = useState(true);
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  const navigate = useNavigate();
  const [openFlashcardsBank, setOpenFlashcardsBank] = useState(false);
  const videoUrl = lessonVideoMapping(lessonType);

  const rippleBorder = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(237, 49, 69, 0.4);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 15px rgba(237, 49, 69, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(237, 49, 69, 0);
  }
`;

  useEffect(() => {
    if (localStorage.getItem("videoAnimationClicked") === "true") {
      setShowAnimation(false);
    }
  }, []);

  const handleVideoClick = () => {
    setOpenVideoDialog(true);
    localStorage.setItem("videoAnimationClicked", "true");
    setShowAnimation(false);
  };

  const handleExamplesClick = () => {
    setLoading(true);

    let targetUrl = "";

    if (quiz) {
      if (user.accountType === "teacher") {
        onExampleClick();
        return;
      } else targetUrl = "/app/quiz/rP02wdkpZNzVkKtnks8i/matematik-exempel";
    } else if (flashcards) {
      targetUrl = "/app/flashcards/ktuJQADPSMGPegXpp9gO/franska-revolutionen";
    } else if (hp) {
      targetUrl = "/app/hp/4miSuszFn20l91NdCMGB";
    } else if (assistent) {
      targetUrl = "/app/personal";
    } else if (grading) {
      targetUrl = "/app/uppsats/Fd0Tga66MLf1TsRv1xid";
    } else if (vision) {
      navigate("/app/vision/vFm2T2spX4");
      setLoading(false);
      return;
    } else {
      console.log("lessonType: ", lessonType);
      if (lessonType === "LessonPlanRevamp.js") {
        targetUrl = "/app/lektioner/lektionsplan/9sNMqcKh0S";
      } else if (lessonType === "JeopardyLesson.js") {
        targetUrl = "/app/lektioner/jeopardy/pitFI12lnL";
      } else if (lessonType === "TimelineLesson.js") {
        targetUrl = "/app/lektioner/tidslinjer/7lxmZwqc8l";
      } else if (lessonType === "BasicReadingCompLesson.js") {
        targetUrl = "/app/lektioner/lasforstaelse/N46W8qE4wP";
      } else if (lessonType === "BasicReadingCompLessonVariantFactText.js") {
        targetUrl = "/app/lektioner/lasforstaelse/sfzkAeuJ22?type=factText";
      } else if (
        lessonType === "BasicReadingCompLessonVariantInstructingText.js"
      ) {
        targetUrl =
          "/app/lektioner/lasforstaelse/A6TAZCV7fU?type=instructingText";
      } else if (
        lessonType === "BasicReadingCompLessonVariantExplainingText.js"
      ) {
        targetUrl =
          "/app/lektioner/lasforstaelse/7XOuiEc3kD?type=explainingText";
      } else if (
        lessonType === "BasicReadingCompLessonVariantArgumentativeText.js"
      ) {
        targetUrl =
          "/app/lektioner/lasforstaelse/ug3txMBUZY?type=argumentativeText";
      } else if (
        lessonType === "BasicReadingCompLessonVariantDescriptiveText.js"
      ) {
        targetUrl =
          "/app/lektioner/lasforstaelse/8RrTHhJz1E?type=descriptiveText";
      } else if (lessonType === "MindMapLesson.js") {
        targetUrl = "/app/lektioner/tankekartor/I7qKiME1td";
      } else if (lessonType === "RolePlayLesson.js") {
        targetUrl = "/app/lektioner/rollkort/29hmsRNaZ6";
      } else if (lessonType === "EscapeRoomLesson.js") {
        targetUrl = "/app/lektioner/escape-room/94u26m0hqB";
      } else if (lessonType === "DiaryLesson.js") {
        targetUrl = "/app/lektioner/fiktiva-dagbocker/jyUXRz9SHp";
      } else if (lessonType === "HistoricPersonLesson.js") {
        targetUrl = "/app/lektioner/historisk-person/rDSrOCAa9p";
      } else if (lessonType === "LettersLesson.js") {
        targetUrl = "/app/lektioner/fiktiva-brev/1ec35VP3ju";
      } else if (lessonType === "LiteratureSchool.js") {
        targetUrl = "/app/lektioner/litteraturskolan/9J4GY1ElLo";
      } else if (lessonType === "TimelineBuilderLesson.js") {
        targetUrl = "/app/lektioner/bygg-tidslinjer/79n31gpSHk";
      } else if (lessonType === "NewsLesson.js") {
        targetUrl = "/app/lektioner/fiktiva-nyhetsredaktionen/2GKNnMWwW0";
      } else if (lessonType === "GrejOfTheDay.js") {
        targetUrl = "/app/lektioner/grej-of-the-day/y6zglZOLT1";
      } else if (lessonType === "ConversationLesson.js") {
        targetUrl = "/app/lektioner/konversation/8PEm3ZbYmW";
      } else if (lessonType === "ConversationLessonVariantInterview.js") {
        targetUrl = "/app/lektioner/konversation/byxjKAln0Q?type=interview";
      } else if (lessonType === "ClueGeneratorLesson.js") {
        targetUrl = "/app/lektioner/pa-sparet/AVGAYBUbn1";
      } else if (lessonType === "SentenceFinisherLesson.js") {
        targetUrl = "/app/lektioner/fortsatt-berattelsen/rc8jQ0hkSX";
      } else if (lessonType === "NarrativeLesson.js") {
        targetUrl = "/app/lektioner/berattande-text/zU5Vbfitpa";
      } else if (lessonType === "TermsGridLesson.js") {
        targetUrl = "/app/lektioner/glosor-i-lador/1mRLhokexN";
      } else if (lessonType === "LetterBoxLesson.js") {
        targetUrl = "/app/lektioner/bokstaver/GTwb6hw4is";
      } else if (lessonType === "FindWordsLesson.js") {
        targetUrl = "/app/lektioner/hitta-orden/n1p0y8r2ge";
      } else if (lessonType === "ArgumentationLesson.js") {
        targetUrl = "/app/lektioner/argumentera/vTG2ix77SV";
      } else if (lessonType === "WeeklyWordsLesson.js") {
        targetUrl = "/app/lektioner/veckans-ord/YeSn4Pc1yp";
      } else {
        setSwitchLessonDrawerOpen(true);
        setLoading(false);
        return;
      }
    }

    // If we're already at the target URL, force a reload by first navigating
    // to a temporary route and then to the actual target
    if (location.pathname === targetUrl) {
      // Creating a timestamp to make the URL unique
      const tempUrl = `/app/tmp-r?t=${Date.now()}`;

      // First navigate to temporary URL then to the actual target
      navigate(tempUrl, { replace: true });

      // Add a slight delay before navigating to the final destination
      setTimeout(() => {
        navigate(targetUrl);
        setLoading(false);
      }, 10);
    } else {
      // Normal navigation
      navigate(targetUrl);
      setLoading(false);
    }
  };
  return (
    <Box sx={{ display: lessonType ? "block" : "block" }}>
      {teachersGuide && (
        <Box sx={{ position: "absolute", top: 20, right: 20, zIndex: 1 }}>
          <TeachersGuide guideKey={teachersGuide} theme={theme} user={user} />
        </Box>
      )}

      <Box
        sx={{
          ...styles.freshBox,

          padding: "0px",
          bgcolor: "background.paper",
          borderRadius: "12px",
          overflow: "hidden",
          width: {
            xs: "100%",
            md:
              textLabbet ||
              powerpoint ||
              podcast ||
              assistent ||
              hp ||
              lessonType === "LiteratureSchool.js" ||
              lessonType === "ClueGeneratorLesson.js"
                ? "200px"
                : lessonType
                  ? "370px"
                  : "370px",
          },
          margin: "0 auto",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs:
                textLabbet ||
                powerpoint ||
                podcast ||
                assistent ||
                hp ||
                lessonType === "LiteratureSchool.js" ||
                lessonType === "ClueGeneratorLesson.js"
                  ? "1fr"
                  : lessonType
                    ? "1fr 1fr "
                    : "1fr 1fr",
              md:
                textLabbet ||
                powerpoint ||
                podcast ||
                assistent ||
                hp ||
                lessonType === "LiteratureSchool.js" ||
                lessonType === "ClueGeneratorLesson.js"
                  ? "1fr"
                  : lessonType
                    ? "1fr 1fr "
                    : "1fr 1fr",
            },
            "& > :first-of-type": {
              width:
                textLabbet ||
                powerpoint ||
                podcast ||
                assistent ||
                hp ||
                lessonType === "LiteratureSchool.js" ||
                lessonType === "ClueGeneratorLesson.js"
                  ? "100%"
                  : "auto",
            },
          }}
        >
          {/* Left side - Video section */}
          {!hp &&
            lessonType !== "LiteratureSchool.js" &&
            lessonType !== "ClueGeneratorLesson.js" && (
              <Box
                onClick={handleVideoClick}
                sx={{
                  p: 2,
                  pt: 1.3,
                  pb: 1.3,
                  position: "relative",
                  overflow: "hidden",
                  cursor: "pointer",
                  transition:
                    "background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  borderRight: `1px solid ${theme.palette.divider}`,
                  "&:hover": {
                    bgcolor: "rgba(103, 58, 183, 0.04)",
                    "& .action-button": {
                      bgcolor: "primary.main",
                      color: "white",
                      borderColor: "primary.main",
                    },
                    "& .ico": {
                      color: "primary.main",
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  {/* Icon container */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        display: "inline-flex",

                        position: "relative",

                        "&::after": showAnimation
                          ? {
                              content: '""',
                              position: "absolute",
                              inset: 0,
                              borderRadius: "inherit",

                              // animation: `${rippleBorder} 2s cubic-bezier(.25,.46,.45,.94) infinite`,
                            }
                          : {},
                      }}
                    >
                      <QuestionMarkIcon
                        className="ico"
                        sx={{
                          width: 18,
                          height: 18,
                          color: "text.secondary",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 500,
                        color: "text.secondary",
                        ml: 0,
                        fontSize: { xs: "0.9rem", md: "0.9rem" },
                      }}
                    >
                      Hjälpguide
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                      mb: 0,
                      display: "none",
                    }}
                  >
                    Se videos om hur det går till
                  </Typography>

                  <Link
                    component="button"
                    className="action-button"
                    sx={{
                      //display: "flex",
                      display: "none",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: "transparent",
                      border: "1px solid",
                      borderColor: "grey.300",
                      color: "text.primary",
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      pointerEvents: "none",
                      textDecoration: "none",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Se video
                  </Link>
                </Box>
              </Box>
            )}

          {/* Right side - Mina lektioner section */}
          {!powerpoint && (
            <Box
              onClick={() => navigate("/app/delade-lektioner")}
              sx={{
                p: 2,
                pt: 1.3,
                pb: 1.3,
                position: "relative",
                //display: "flex",
                display: "none",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                overflow: "hidden",
                cursor: "pointer",
                transition:
                  "background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                borderRight: `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  bgcolor: "rgba(103, 58, 183, 0.04)",
                  "& .action-button": {
                    bgcolor: "primary.main",
                    color: "white",
                    borderColor: "primary.main",
                  },
                },
              }}
            >
              <Box sx={{ position: "relative" }}>
                {/* Icon container */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      bgcolor: "grey.200",
                      borderRadius: 30,
                      p: 0.7,
                    }}
                  >
                    <FolderIcon
                      sx={{
                        width: 15,
                        height: 15,
                        color: "text.secondary",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 500,
                      color: "text.secondary",
                      ml: 1,
                      fontSize: { xs: "0.9rem", md: "0.9rem" },
                    }}
                  >
                    Mina lektioner
                  </Typography>
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    mb: 0,
                    display: "none",
                  }}
                >
                  Se dina sparade lektioner
                </Typography>

                <Link
                  component="button"
                  className="action-button"
                  sx={{
                    display: "none",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "transparent",
                    border: "1px solid",
                    borderColor: "grey.300",
                    color: "text.primary",
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    pointerEvents: "none",
                    textDecoration: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  Mina lektioner
                </Link>
              </Box>
            </Box>
          )}

          {/* Right side - Examples section */}
          {!textLabbet && !powerpoint && !podcast && !assistent && (
            <Box
              onClick={handleExamplesClick}
              // For the right (examples) side Box
              sx={{
                p: 2,
                pt: 1.3,
                pb: 1.3,

                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                overflow: "hidden",
                cursor: "pointer",
                transition:
                  "background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  bgcolor: "rgba(103, 58, 183, 0.04)",
                  "& .action-button": {
                    bgcolor: "primary.main",
                    color: "white",
                    borderColor: "primary.main",
                  },
                  "& .ico": {
                    color: "primary.main",
                  },
                },
              }}
            >
              <Box sx={{ position: "relative" }}>
                {/* Icon container */}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                    }}
                  >
                    {loadingExample ? (
                      <CircularProgress
                        size={15}
                        sx={{
                          color: "text.secondary",
                        }}
                      />
                    ) : quiz || flashcards ? (
                      <LightbulbIcon
                        className="ico"
                        sx={{
                          width: 18,
                          height: 18,
                          color: "text.secondary",
                        }}
                      />
                    ) : (
                      <LightbulbIcon
                        className="ico"
                        sx={{
                          width: 18,
                          height: 18,
                          color: "text.secondary",
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 500,
                      color: "text.secondary",
                      ml: 0,
                      fontSize: { xs: "0.9rem", md: "0.9rem" },
                    }}
                  >
                    {quiz
                      ? "Visa exempel"
                      : flashcards
                        ? "Visa exempel"
                        : "Visa exempel"}
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    mb: 0,
                    display: "none",
                  }}
                >
                  {grading
                    ? "Öppnas i ny flik"
                    : vision
                      ? "Öppnas i ny flik"
                      : "Inspireras av vad andra har genererat"}
                </Typography>

                <Button
                  className="action-button"
                  sx={{
                    //display: "flex",
                    display: "none",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "transparent",
                    border: "1px solid",
                    borderColor: "grey.300",
                    color: "text.primary",
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    pointerEvents: "none",
                    textDecoration: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  Visa exempel
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <HelpDialog
        open={openVideoDialog}
        onClose={() => setOpenVideoDialog(false)}
        quiz={quiz}
        flashcards={flashcards}
        textLabbet={textLabbet}
        grading={grading}
        powerpoint={powerpoint}
        assistent={assistent}
        vision={vision}
        lessonPlan={lessonPlan}
        podcast={podcast}
        lessonType={lessonType}
        user={user}
        controls={true}
      />

      <SwitchLessonsDrawer
        open={switchLessonDrawerOpen}
        onClose={() => setSwitchLessonDrawerOpen(false)}
        user={user}
        styles={styles}
        theme={theme}
        lessonType={lessonType}
      />

      <LessonBankDialog
        open={openLessonBank}
        onClose={() => setOpenLessonBank(false)}
        user={user}
        styles={styles}
        theme={theme}
        lessonType={lessonType}
      />

      <QuizBank
        open={openQuizBank}
        onClose={() => setOpenQuizBank(false)}
        user={user}
        styles={styles}
        theme={theme}
      />

      <FlashcardsBank
        open={openFlashcardsBank}
        onClose={() => setOpenFlashcardsBank(false)}
        user={user}
        styles={styles}
        theme={theme}
      />
    </Box>
  );
};

export default InfoBox;
